import React from "react";
import { Link } from "react-router-dom";
import { Mountain, Mail, Phone, MapPin } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const Footer = () => {
  const { t, localePath } = useI18n();

  const navItems = [
    { key: "sector", hash: "#sector" },
    { key: "process", hash: "#process" },
    { key: "transparency", hash: "#transparency" },
    { key: "subscribe", hash: "#subscribe" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B2545] text-slate-200 border-t border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Link
              to={localePath("/")}
              data-testid="footer-logo-link"
              className="flex items-center gap-3"
            >
              <span className="w-9 h-9 flex items-center justify-center bg-[#C85A32] text-white">
                <Mountain size={18} strokeWidth={2.25} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-lg font-bold tracking-tight text-white">
                  {t("brand.name")}
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  {t("brand.tagline")}
                </span>
              </span>
            </Link>
            <p className="mt-5 text-sm text-slate-300 leading-relaxed max-w-md">
              {t("footer.companyDescription")}
            </p>

            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-[#C85A32]" />
                <span>{t("footer.legal.address")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={14} className="text-[#C85A32]" />
                <a
                  href={`tel:${t("footer.legal.phone").replace(/\s|\+/g, "")}`}
                  data-testid="footer-phone"
                  className="hover:text-white"
                >
                  {t("footer.legal.phone")}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} className="text-[#C85A32]" />
                <a
                  href={`mailto:${t("footer.legal.email")}`}
                  data-testid="footer-email"
                  className="hover:text-white"
                >
                  {t("footer.legal.email")}
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              {t("footer.sections.navigation")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              {navItems.map((item) => (
                <li key={item.key}>
                  <a
                    href={`${localePath("/")}${item.hash}`}
                    data-testid={`footer-nav-${item.key}`}
                    className="text-slate-300 hover:text-white"
                  >
                    {t(`nav.${item.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              {t("footer.sections.legal")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={localePath("/privacy")}
                  data-testid="footer-link-privacy"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/cookies")}
                  data-testid="footer-link-cookies"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/terms")}
                  data-testid="footer-link-terms"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
            </ul>

            <div className="mt-6 border-t border-slate-800 pt-5 text-xs text-slate-400 space-y-1.5">
              <p className="font-semibold text-slate-200">{t("footer.legal.company")}</p>
              <p>{t("footer.legal.cuit")}</p>
              <p>{t("footer.legal.igj")}</p>
              <p>{t("footer.legal.license")}</p>
              <p>{t("footer.legal.jurisdiction")}</p>
              <p>{t("footer.legal.incorporation")}</p>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-slate-800 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-slate-400" data-testid="footer-copyright">
            {t("footer.copyright")}
          </p>
          <p className="text-xs text-slate-500 md:text-right max-w-2xl">
            {t("footer.disclaimer")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
